// GARAN label: the short form is a button that opens the full label in a modal.
// One shared <dialog> on <body> serves every label on the page, so it also works
// inside the cart drawer, which is rebuilt after each cart change. Living outside
// the drawer keeps Escape and clicks in the modal from closing the drawer.
if (!customElements.get('garan-label')) {
  customElements.define(
    'garan-label',
    class GaranLabel extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('.garan__trigger');
        this.template = this.querySelector('template');
        if (!this.trigger || !this.template) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        // Build the modal content once the label is near the viewport so the image is
        // already loaded: the full label has to appear on the first click or tap.
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.prepare();
            this.observer.disconnect();
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
        this.trigger.addEventListener('click', () => this.open(), { signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.observer?.disconnect();
      }

      prepare() {
        this.content ??= document.importNode(this.template.content, true);
      }

      open() {
        this.prepare();
        const dialog = GaranLabel.dialog();
        dialog.querySelector('.garan-dialog__inner').replaceChildren(this.content.cloneNode(true));
        dialog.returnFocus = this.trigger;
        dialog.showModal();
        dialog.querySelector('[data-garan-close]')?.focus();
      }

      static dialog() {
        let dialog = document.getElementById('GaranDialog');
        if (dialog) return dialog;

        dialog = document.createElement('dialog');
        dialog.id = 'GaranDialog';
        dialog.className = 'garan-dialog';
        dialog.setAttribute('aria-labelledby', 'GaranDialogTitle');
        dialog.innerHTML = '<div class="garan-dialog__inner"></div>';

        dialog.addEventListener('click', (event) => {
          // A click on the backdrop lands on the dialog element itself.
          if (event.target === dialog || event.target.closest('[data-garan-close]')) dialog.close();
        });
        dialog.addEventListener('close', () => {
          if (dialog.returnFocus?.isConnected) dialog.returnFocus.focus();
        });
        // Escape closes the dialog on keydown and focus returns to the trigger. The
        // keyup that follows would then reach the cart drawer, which closes on an
        // Escape keyup, so that one keyup is swallowed.
        dialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          window.addEventListener(
            'keyup',
            (keyup) => keyup.key === 'Escape' && keyup.stopImmediatePropagation(),
            { capture: true, once: true }
          );
        });

        document.body.append(dialog);
        return dialog;
      }
    }
  );
}
